import axios from 'axios';
import get from 'lodash/get';
import { getJwtToken } from '../../services/auth';

import { Amplify } from '@aws-amplify/core';
import config from '../../config';

Amplify.configure(config);

/**
 * CRCE technical heat profile API (PQT import list, etc.).
 * Base URL resolution (first non-empty wins):
 * 1. `REACT_APP_AWS_CRCE_TECHNICAL_HEAT_PROFILE` — dedicated technical API (preferred in dev).
 * 2. `REACT_APP_AWS_HEAT_PROFILE_DATABASE` — same host as the main heat-profile service; use when
 *    the technical list is served from that service and you do not set the CRCE-specific var in CI.
 * If both are empty, requests resolve to the SPA host and you get 404 on `/technical-heat-profile`.
 */
const explicitCrce = (process.env.REACT_APP_AWS_CRCE_TECHNICAL_HEAT_PROFILE || '').trim();
const heatProfileDb = (process.env.REACT_APP_AWS_HEAT_PROFILE_DATABASE || '').trim();
const crceRaw = explicitCrce || heatProfileDb;
export const isCrceTechnicalHeatProfileApiConfigured = crceRaw.length > 0;
const baseURL = isCrceTechnicalHeatProfileApiConfigured ? crceRaw.replace(/\/?$/, '/') : '/';

const crceTechnicalHeatProfileAxios = axios.create({
  baseURL,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

const handleErroredResponse = async (error) => {
  const originalRequest = get(error, ['response', 'config'], undefined);
  const statusCode = get(error, ['response', 'status'], undefined);
  const errMessage = get(error, ['response', 'data', 'name'], undefined);
  const errData = get(error, ['response', 'data'], undefined);

  if (statusCode === 401) {
    if (errMessage === 'TokenExpiredError') {
      const token = await getJwtToken();
      if (token) {
        axios.defaults.headers.common['Authorization'] = token;
      }
      return crceTechnicalHeatProfileAxios(originalRequest);
    }
  }

  if (errData) {
    return Promise.reject(errData);
  }
  return Promise.reject(error.message);
};

crceTechnicalHeatProfileAxios.interceptors.request.use(
  async (config) => {
    if (!isCrceTechnicalHeatProfileApiConfigured) {
      return Promise.reject(
        new Error(
          'Neither REACT_APP_AWS_CRCE_TECHNICAL_HEAT_PROFILE nor REACT_APP_AWS_HEAT_PROFILE_DATABASE is set in this build. Set at least one (in .env or pipeline), then rebuild the frontend.'
        )
      );
    }
    const aConfig = config;
    const authToken = await getJwtToken();
    if (authToken) {
      aConfig.headers['Authorization'] = authToken;
    }
    return aConfig;
  },
  (error) => Promise.reject(error)
);

crceTechnicalHeatProfileAxios.interceptors.response.use(
  (response) => response,
  async (error) => handleErroredResponse(error)
);

export default crceTechnicalHeatProfileAxios;
